import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Input, Card } from '@/design-system';
import { apiPatch } from '@/infrastructure/api-client';
import { useAuthStore } from '@/features/auth/state/auth-store';
import type { ProfileResponse } from '@/contracts/auth';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * SPEC-002 §35/§36/§23: view + edit display_name/locale/timezone; email is
 * read-only (PD-006); avatar falls back to initials (upload deferred —
 * plan §22 "Decisão do plano").
 */
export function ProfilePage() {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [locale, setLocale] = useState(user?.locale ?? 'pt-BR');
  const [timezone, setTimezone] = useState(user?.timezone ?? '');
  const [saveState, setSaveState] = useState<SaveState>('idle');

  if (!user) {
    return null;
  }

  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaveState('saving');
    try {
      const response = await apiPatch<ProfileResponse>('/api/profile', {
        displayName,
        locale,
        timezone: timezone || null,
      });
      setUser(response.user);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }

  function detectTimezone() {
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }

  return (
    <main className="mx-auto flex max-w-lg flex-col gap-6 px-4 py-10">
      <h1 className="text-2xl font-semibold text-text-primary">Perfil</h1>

      <Card className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-lg font-semibold text-[#070807]">
          {initials || '?'}
        </div>
        <div>
          <p className="text-sm font-medium text-text-primary">{user.displayName}</p>
          <p className="text-xs text-text-muted">{user.email}</p>
        </div>
      </Card>

      <Card>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Identidade</h2>

          <Input
            label="Nome de exibição"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />

          <Input label="Email" value={user.email} disabled readOnly />

          <h2 className="mt-2 text-sm font-semibold uppercase tracking-wide text-text-muted">
            Preferências
          </h2>

          <Input label="Localidade" value={locale} onChange={(e) => setLocale(e.target.value)} />

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Fuso horário"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="ex.: America/Sao_Paulo"
              />
            </div>
            <Button type="button" variant="secondary" onClick={detectTimezone}>
              Detectar
            </Button>
          </div>

          <h2 className="mt-2 text-sm font-semibold uppercase tracking-wide text-text-muted">
            Informações de segurança
          </h2>
          <p className="text-xs text-text-muted">
            Último acesso:{' '}
            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString(locale) : 'desconhecido'}
          </p>

          <Button type="submit" disabled={saveState === 'saving'}>
            {saveState === 'saving' ? 'Salvando…' : 'Salvar alterações'}
          </Button>
          {saveState === 'saved' ? (
            <p className="text-sm text-[#38c980]">Salvo.</p>
          ) : saveState === 'error' ? (
            <p role="alert" className="text-sm text-[#f08484]">
              Não foi possível salvar suas alterações.
            </p>
          ) : null}
        </form>
      </Card>
    </main>
  );
}
